import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, QrCode as QrIcon, ExternalLink, Printer } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const qrUrl = (data) => `https://api.qrserver.com/v1/create-qr-code/?size=240x240&margin=8&data=${encodeURIComponent(data)}`;

export default function TablesManager({ restaurant }) {
  const [tables, setTables] = useState([]);
  const [label, setLabel] = useState("");
  const [seats, setSeats] = useState(2);

  const load = async () => {
    const { data } = await api.get("/tables");
    setTables(data);
  };
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!label.trim()) return;
    try {
      await api.post("/tables", { label, seats: Number(seats) || 2 });
      setLabel("");
      await load();
      toast.success("Tavolo aggiunto");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const del = async (id) => {
    if (!window.confirm("Eliminare il tavolo?")) return;
    await api.delete(`/tables/${id}`);
    await load();
  };

  const tableUrl = (t) => `${window.location.origin}/r/${restaurant?.id}/t/${t.id}`;

  return (
    <div className="space-y-8" data-testid="tables-page">
      <div>
        <div className="text-xs uppercase tracking-widest text-muted-foreground">Tavoli</div>
        <h1 className="font-display text-4xl font-black tracking-tighter mt-1">Tavoli &amp; QR code</h1>
      </div>

      <div className="p-6 bg-card border border-border rounded-2xl">
        <div className="flex flex-col md:flex-row gap-3 md:items-end">
          <div className="flex-1">
            <Label>Etichetta (es. Tavolo 1)</Label>
            <Input value={label} onChange={(e) => setLabel(e.target.value)} data-testid="table-label-input" />
          </div>
          <div className="w-full md:w-32">
            <Label>Posti</Label>
            <Input type="number" min="1" value={seats} onChange={(e) => setSeats(e.target.value)} data-testid="table-seats-input" />
          </div>
          <Button onClick={add} className="pill-btn bg-primary text-primary-foreground hover:bg-[hsl(var(--accent))]" data-testid="add-table-btn">
            <Plus className="h-4 w-4 mr-2" /> Aggiungi
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {tables.map((t) => (
          <div key={t.id} className="p-6 bg-card border border-border rounded-2xl flex flex-col items-center gap-3" data-testid={`table-card-${t.id}`}>
            <div className="w-full flex justify-between items-start">
              <div>
                <div className="font-display text-2xl font-bold">{t.label}</div>
                <div className="text-xs text-muted-foreground">{t.seats} posti</div>
              </div>
              <Button variant="ghost" size="sm" onClick={() => del(t.id)} data-testid={`del-table-${t.id}`}>
                <Trash2 className="h-3 w-3" />
              </Button>
            </div>
            <img src={qrUrl(tableUrl(t))} alt="QR" className="w-40 h-40 rounded-lg bg-white" />
            <div className="flex gap-2 w-full">
              <a href={tableUrl(t)} target="_blank" rel="noreferrer" className="flex-1">
                <Button size="sm" variant="outline" className="w-full" data-testid={`open-table-${t.id}`}>
                  <ExternalLink className="h-3 w-3 mr-1" /> Apri
                </Button>
              </a>
              <a href={qrUrl(tableUrl(t))} target="_blank" rel="noreferrer" className="flex-1">
                <Button size="sm" variant="outline" className="w-full" data-testid={`print-table-${t.id}`}>
                  <Printer className="h-3 w-3 mr-1" /> Stampa
                </Button>
              </a>
            </div>
          </div>
        ))}
        {tables.length === 0 && (
          <div className="col-span-full p-12 text-center text-muted-foreground border border-dashed rounded-2xl">
            Nessun tavolo. Aggiungi il primo.
          </div>
        )}
      </div>
    </div>
  );
}
